import React, { useState, useEffect, useCallback } from "react";
import { Button, Input, Form, Select, Tooltip } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import leftArrow from "../../../assets/images/LeftArrow.svg";
import { toast } from "react-toastify";
import UserIcon from "../../../assets/images/user-icon.svg";
import { getFormFieldValue } from "../../../utils/index";
import ImageLabel from "../../image-label";
import { formatData } from "../../../utils/formatData";
import { useStore } from "../../../store";
import "../index.scss";
import ModalMediaPlayer from "../../videoPlayer/modalPlayer";
import { useConnectsUForm } from "../../../custom-hooks/useConnectsUForm";
import { setUpdatedValues } from "../../../store/actions";
import { useTab } from "../../../custom-hooks/useTab";
import PlusIcon from "../../../assets/images/plus-icon.svg";
import QuestionIcon from "../../../assets/images/question-icon.svg";
import deleteIcon from "../../../assets/images/delete-icon.svg";
import { endUserAssetTypeOptions, orgAdminAssetTypeOptions, partnerAdminAssetTypeOptions } from "./HelpConstants";

const { TextArea } = Input;

const isHelpRowComplete = (row, index, form, userRole) => {
  if (!row || typeof row !== "object") return false;
  if (!row.title?.trim()) return false;
  if (!row.description?.trim()) return false;

  const mediaType = row.mediaLink?.mediaType;
  if (!mediaType) return false;

  if (mediaType === "IMAGE") {
    const blob = form.getFieldValue(["help", index, "blob"]);
    const imageUrl = row.mediaLink?.url;
    if (!blob && !imageUrl) return false;
  } else if (
    mediaType === "SOCIAL_MEDIA" ||
    mediaType === "VIMEO_MEDIA" ||
    mediaType === "SYNTHESIA_MEDIA"
  ) {
    if (!row.mediaLink?.url?.trim()) return false;
  }

  if (!userRole) return false;
  if (!row.assetTypeEnum) return false;
  if (row.assetTypeEnum === "OTHER" && !row.assetTypeName?.trim()) {
    return false;
  }

  return true;
};

export default function AddHelp() {
  const navigate = useNavigate();
  const { state } = useLocation() || {};
  const [mediatype] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const whichTab = "help";
  const { updateImage } = useConnectsUForm();
  const { updatedValues, dispatch } = useStore();
  const [canSave, setCanSave] = useState(false);
  const { callMicroServices } = useTab();
  const [userRole, setUserRole] = useState();
  const [isBtnDisabled, setIsBtnDisabled] = useState(false);
  const whyApplyTaken = Boolean(state?.whyApplyTaken);
  const helpRows = Form.useWatch("help", form);

  const getOrgAdminOptions = (rowIndex) => {
    const takenOnAnotherRow = helpRows?.some(
      (row, index) => index !== rowIndex && row?.assetTypeEnum === "WHY_APPLY"
    );
    if (whyApplyTaken || takenOnAnotherRow) {
      return orgAdminAssetTypeOptions.filter((option) => option.value !== "WHY_APPLY");
    }
    return orgAdminAssetTypeOptions;
  };

  useEffect(() => {
    setUserRole(state?.userRole);
  }, [state?.userRole]);

  useEffect(() => {
    dispatch(setUpdatedValues({}));
  }, [dispatch]);

  const updateSaveButtonState = useCallback(() => {
    const help = form.getFieldValue("help");
    if (!help?.length) {
      setCanSave(false);
      return;
    }
    const allRowsComplete = help.every((row, index) =>
      isHelpRowComplete(row, index, form, userRole)
    );
    setCanSave(allRowsComplete);
  }, [form, userRole]);

  useEffect(() => {
    updateSaveButtonState();
  }, [userRole, updateSaveButtonState]);

  const handleCancel = () => {
    setIsModalOpen(false);
    setVideoUrl("");
  };

  const findValue = (i, key) => {
    return getFormFieldValue(form, "help", i, key);
  };

  const SelectChangeHandler = (mediaType, i) => {
    form.setFieldValue([whichTab, i, "mediaLink", "url"], undefined);
    form.setFieldValue([whichTab, i, "mediaLink", "mediaType"], mediaType);
    form.setFieldValue([whichTab, i, "blob"], undefined);
    setTimeout(updateSaveButtonState, 0);
  };

  const handleImageUpload = (event, i) => {
    updateImage(event, i, form, whichTab);
    setTimeout(updateSaveButtonState, 0);
  };

  const removeHelpRow = (fieldName, removeFn) => {
    removeFn(fieldName);
    const prevHelp = updatedValues.help;
    if (prevHelp?.length) {
      const nextHelp = prevHelp
        .filter((idx) => idx !== fieldName)
        .map((idx) => (idx > fieldName ? idx - 1 : idx));

      const next = { ...updatedValues };
      if (nextHelp.length) {
        next.help = nextHelp;
      } else {
        delete next.help;
      }
      dispatch(setUpdatedValues(next));
    }
    setTimeout(updateSaveButtonState, 0);
  };

  // Clears the "otherAssetType" text field whenever the user picks
  // something other than "OTHER" in the Asset Type dropdown.
  const AssetTypeChangeHandler = (value, name) => {
    if (value !== "OTHER") {
      form.setFieldValue([whichTab, name, "otherAssetType"], undefined);
    }
  };

  const fieldsChange = (fields) => {
    if (fields && fields.length === 1) {
      const getNames = fields[0].name;

      const nameModified =
        getNames[0] === "corousel" ? "corousel" : getNames[0];

      const prevItems = updatedValues[nameModified];
      let nextItems;

      if (prevItems) {
        const names = [...prevItems, getNames[1]];
        nextItems = names.filter(
          (item, index, inputArray) => inputArray.indexOf(item) === index
        );
      } else {
        nextItems = [getNames[1]];
      }

      dispatch(
        setUpdatedValues({
          ...updatedValues,
          [nameModified]: nextItems,
        })
      );
    }
    setTimeout(updateSaveButtonState, 0);
  };

  const handleUpdateItem = async (data) => {
    setIsBtnDisabled(true);
    try {
      if (!data?.help) {
        return;
      }
      if (!userRole) {
        return;
      }

      const formattedData = formatData(
        "help",
        data.help,
        updatedValues,
        userRole
      );

      if (Object.keys(formattedData).length === 0) {
        toast.error("Nothing to update");
        return;
      }

      const response = await callMicroServices("help", formattedData, form);

      if (response) {
        dispatch(setUpdatedValues({}));
        setCanSave(false);
        navigate("/admin", { state: { reload: true } });
      } else {
        setTimeout(updateSaveButtonState, 0);
      }
    } finally {
      setIsBtnDisabled(false);
    }
  };

  return (
    <div>
      <div
        className="heading-backBtn"
        onClick={() => {
          navigate("/admin", { state: { reload: false } });
          sessionStorage.removeItem("helpDataResponse");
        }}
      >
        <img style={{ marginRight: "8px" }} src={leftArrow} alt="leftarrow" />
        Add Help
      </div>


      <div className="edit-video-container multipalFormStyle">
        <Form
          name="control-hooks"
          className="organizationForm"
          form={form}
          autoComplete="off"
          labelCol={{ span: 5 }}
          onFinish={handleUpdateItem}
          onFieldsChange={fieldsChange}
        >
          <div className="multipalFormRow">
            <div className="multipalFormCol">
              <Form.List name="help" initialValue={[""]}>
                {(fields, { add, remove }) => (
                  <>
                    <div className="formListRow">
                      <div className="formListCol">
                        <div style={{ textAlign: "right" }}>
                          <Button
                            className="redBtn"
                            onClick={() => {
                              add();
                              setTimeout(updateSaveButtonState, 0);
                            }}
                          >
                            <img
                              src={PlusIcon}
                              alt="plusIcon"
                              style={{ marginRight: "5px" }}
                            />
                            Add More
                          </Button>
                        </div>
                      </div>
                    </div>
                    {fields.map(({ key, name, ...restField }, i) => (

                      <div className="formListRow" key={key}>
                        <div className="formListCol">
                          <div className="d-flex">
                            <Form.Item
                              {...restField}
                              className="edit-videos-title"
                              label="Title"
                              name={[name, "title"]}
                              style={{ minWidth: "100%", display: "block" }}
                              rules={[
                                {
                                  required: true,
                                  message: "Title is required",
                                },
                              ]}
                            >
                              <Input
                                placeholder="Title"
                                maxLength={30}
                                showCount
                              />
                            </Form.Item>
                            {i > 0 && (
                              <Form.Item style={{ marginLeft: "5px" }}>
                                <div className="edit-remove-btn">
                                  <img
                                    src={deleteIcon}
                                    alt="delete"
                                    onClick={() => removeHelpRow(name, remove)}
                                    style={{ cursor: "pointer" }}
                                  />
                                </div>
                              </Form.Item>
                            )}
                          </div>

                          <Form.Item
                            {...restField}
                            className="edit-videos-title"
                            label="Description"
                            name={[name, "description"]}
                            rules={[
                              {
                                required: true,
                                message: "Description is required",
                              },
                              {
                                validator: (_, value) => {
                                  if (value && value.length > 400) {
                                    return Promise.reject(
                                      new Error("Description cannot exceed 400 characters")
                                    );
                                  }
                                  return Promise.resolve();
                                },
                              },
                            ]}
                          >
                            <TextArea
                              placeholder="Description"
                              rows={3}
                              showCount
                              maxLength={400} // allow typing more, validator will catch >400
                            />
                          </Form.Item>

                          <div className="d-flex">
                            <Form.Item
                              {...restField}
                              name={[name, "mediaLink", "mediaType"]}
                              className="edit-videos-title"
                              style={{ minWidth: "100%", display: "block" }}
                              labelCol={{ span: 5 }}
                              label="Image/Video"
                              rules={[
                                {
                                  required: true,
                                  message: "Select any option",
                                },
                              ]}
                            >
                              <Select
                                onChange={(value) => {
                                  SelectChangeHandler(value, i);
                                }}
                                placeholder="Select Option"
                              >
                                <Select.Option value="VIMEO_MEDIA">Add Vimeo Link</Select.Option>
                                <Select.Option value="SOCIAL_MEDIA">Add Youtube Link</Select.Option>
                                <Select.Option value="SYNTHESIA_MEDIA">Add Synthesia Link</Select.Option>
                                <Select.Option value="IMAGE">Upload Image</Select.Option>
                              </Select>
                            </Form.Item>
                          </div>

                          {findValue(i, "type") === "SOCIAL_MEDIA" ? (
                            <Form.Item
                              label="Youtube Link"
                              {...restField}
                              name={[name, "mediaLink", "url"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Youtube link is required",
                                },
                                {
                                  validator: (_, value) => {
                                    if (!value) return Promise.resolve();
                                    const youtubeRegex =
                                      /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/;
                                    return youtubeRegex.test(value)
                                      ? Promise.resolve()
                                      : Promise.reject(new Error("Enter a valid value"));
                                  },
                                },
                              ]}
                            >
                              <Input placeholder="Youtube Link" />
                            </Form.Item>
                          ) : findValue(i, "type") === "VIMEO_MEDIA" ? (
                            <Form.Item
                              label="Vimeo Link"
                              {...restField}
                              name={[name, "mediaLink", "url"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Vimeo link is required",
                                },
                                {
                                  validator: (_, value) => {
                                    if (!value) return Promise.resolve();
                                    const vimeoRegex =
                                      /^(https?:\/\/)?(www\.)?vimeo\.com\/\d+$/;
                                    return vimeoRegex.test(value)
                                      ? Promise.resolve()
                                      : Promise.reject(new Error("Enter a valid value"));
                                  },
                                },
                              ]}
                            >
                              <Input placeholder="Vimeo Link" />
                            </Form.Item>
                          ) : findValue(i, "type") === "IMAGE" ? (
                            <Form.Item
                              {...restField}
                              label={<ImageLabel label={"Image"} />}
                              colon={false}
                              name="mediaLink"
                              className="experienceImg"
                              rules={[
                                {
                                  validator: (_, value) => {
                                    return findValue(i, "blob")
                                      ? Promise.resolve()
                                      : findValue(i, "url")
                                        ? Promise.resolve()
                                        : Promise.reject(
                                          new Error("Enter a valid value")
                                        );
                                  },
                                },
                              ]}
                            >
                              {findValue(i, "blob") ? (
                                <img
                                  src={URL.createObjectURL(
                                    findValue(i, "blob")
                                  )}
                                  alt=""
                                  style={{
                                    maxHeight: "120px",
                                    width: "300px",
                                    border: "1px solid #A5A5A5",
                                    borderRadius: "5px",
                                    objectFit: "contain",
                                    marginBottom: "-10px",
                                    marginRight: "10px",
                                  }}
                                />
                              ) : findValue(i, "url") ? (
                                <img
                                  src={findValue(i, "url")}
                                  alt=""
                                  style={{
                                    maxHeight: "120px",
                                    width: "300px",
                                    border: "1px solid #A5A5A5",
                                    borderRadius: "5px",
                                    objectFit: "contain",
                                    marginBottom: "-10px",
                                    marginRight: "10px",
                                  }}
                                />
                              ) : (
                                <img
                                  alt="UserIcon"
                                  src={UserIcon}
                                  style={{
                                    maxHeight: "80px",
                                    marginRight: "10px",
                                    marginBottom: "-15px",
                                  }}
                                />
                              )}

                              <label
                                name="file"
                                className="fileUploadBtn redBtn"
                                style={{ padding: "8px" }}
                              >
                                {" "}
                                Upload Image
                                <input
                                  accept="image/png, image/gif, image/jpeg"
                                  type="file"
                                  style={{ display: "none" }}
                                  // required={true}
                                  onChange={(e) =>
                                    handleImageUpload(e, i)
                                  }
                                />
                              </label>
                              <Tooltip
                                className="question"
                                title={
                                  "Recommended image dimensions: 1280x720 pixels"
                                }
                              >
                                <img
                                  src={QuestionIcon}
                                  alt="question-icon"
                                />
                              </Tooltip>
                            </Form.Item>
                          ) : findValue(i, "type") === "SYNTHESIA_MEDIA" ? (
                            <Form.Item
                              label="Synthesia Link"
                              labelCol={{ span: 5 }}
                              {...restField}
                              name={[name, "mediaLink", "url"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Synthesia  link is required",
                                },

                              ]}
                            >
                              <Input />
                            </Form.Item>
                          ) : null}

                          <Form.Item
                            {...restField}
                            className="edit-videos-title"
                            label={<span style={{ marginLeft: '10px' }}>Web Link</span>}
                            name={[name, "webLink"]}
                            rules={[
                              {
                                required: false,
                                message: "Web link is required",
                              },
                              {
                                validator: (_, value) => {
                                  if (!value) return Promise.resolve();

                                  // Generic URL regex
                                  const urlRegex =
                                    /^(https?:\/\/)([\w-]+\.)+[\w-]{2,}(\/\S*)?$/;

                                  return urlRegex.test(value)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error("Enter a valid web link"));
                                },
                              },
                            ]}
                          >
                            <Input placeholder="Web link" onChange={(e) => {
                              const value = e.target.value;

                              if (value.length > 200) {
                                toast.warn("Weblink should not exceed 200 characters");

                                form.setFieldValue(
                                  [whichTab, name, "webLink"],
                                  value.slice(0, 200)
                                );
                              }
                            }} />
                          </Form.Item>

                          <div className="d-flex">
                            {userRole === "ORGANIZATION_ADMIN" ? (
                              <Form.Item
                                {...restField}
                                style={{
                                  minWidth: "100%",
                                  display: "block",
                                }}
                                className="edit-videos-title"
                                label="Subject Type"
                                name={[name, "assetTypeEnum"]}
                                rules={[
                                  {
                                    required: true,
                                    message:
                                      "Select one asset typefrom list",
                                  },
                                ]}
                              >
                                <Select
                                  name={[name, "assetTypeEnum"]}
                                  placeholder="Select subject type"
                                  onChange={(value) =>
                                    AssetTypeChangeHandler(value, name)
                                  }
                                >
                                  {getOrgAdminOptions(name)?.map(({ value, label }) => (
                                    <Select.Option key={value} value={value}>
                                      {label}
                                    </Select.Option>
                                  ))}
                                </Select >
                              </Form.Item>
                            ) : userRole === "PARTNER_ADMIN" ? (
                              <Form.Item
                                {...restField}
                                className="edit-videos-title"
                                style={{
                                  minWidth: "100%",
                                  display: "block",
                                }}
                                label="Subject Type"
                                name={[name, "assetTypeEnum"]}
                                rules={[
                                  {
                                    required: true,
                                    message:
                                      "Select one asset typefrom list",
                                  },
                                ]}
                              >
                                <Select
                                  name={[name, "assetTypeEnum"]}
                                  placeholder="Select subject  type"
                                  onChange={(value) =>
                                    AssetTypeChangeHandler(value, name)
                                  }
                                >
                                   {partnerAdminAssetTypeOptions?.map(({ value, label }) => (
                                                                      <Select.Option key={value} value={value}>
                                                                        {label}
                                                                      </Select.Option>
                                                                    ))}
                                
                                </Select>
                              </Form.Item>
                            ) : userRole === "END_USER" ? (
                              <Form.Item
                                {...restField}
                                className="edit-videos-title"
                                style={{
                                  minWidth: "100%",
                                  display: "block",
                                }}
                                label="Subject Type"
                                name={[name, "assetTypeEnum"]}
                                rules={[
                                  {
                                    required: true,
                                    message:
                                      "Select one asset typefrom list",
                                  },
                                ]}
                              >
                                <Select
                                  name={[name, "assetTypeEnum"]}
                                  placeholder="Select subject type"
                                  onChange={(value) =>
                                    AssetTypeChangeHandler(value, name)
                                  }
                                >
                                  {endUserAssetTypeOptions?.map(({ value, label }) => (
                                    <Select.Option key={value} value={value}>
                                      {label}
                                    </Select.Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            ) : null}
                            <Form.Item style={{ marginLeft: "5px" }}>
                              <div className="edit-remove-btn">
                                <Tooltip
                                  title={
                                    "For which section this help content needs to be displayed"
                                  }
                                >
                                  <img
                                    src={QuestionIcon}
                                    alt="question-icon"
                                  />
                                </Tooltip>
                              </div>
                            </Form.Item>
                          </div>

                          <Form.Item
                            noStyle
                            shouldUpdate={(prevValues, curValues) => {
                              const prevType =
                                prevValues?.help?.[name]?.assetTypeEnum;
                              const curType =
                                curValues?.help?.[name]?.assetTypeEnum;
                              return prevType !== curType;
                            }}
                          >
                            {({ getFieldValue }) =>
                              getFieldValue(["help", name, "assetTypeEnum"]) ===
                                "OTHER" ? (
                                <Form.Item
                                  {...restField}
                                  className="edit-videos-title-subject-type hide-label"
                                  label=" "
                                  labelCol={{ span: 5 }}
                                  wrapperCol={{ span: 19 }}
                                  name={[name, "assetTypeName"]}
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please enter a value for Other",
                                    },
                                    {
                                      validator: (_, value) => {
                                        if (value && value.length > 30) {
                                          return Promise.reject(
                                            new Error("Cannot exceed 30 characters")
                                          );
                                        }
                                        return Promise.resolve();
                                      },
                                    },
                                  ]}
                                >
                                  <Input
                                    className="subject-type-input"
                                    placeholder="Enter subject type"
                                    maxLength={30}
                                    showCount
                                  />
                                </Form.Item>
                              ) : null
                            }
                          </Form.Item>{" "}
                        </div>
                      </div>

                    ))}
                    <div className="formListRow">
                      <div className="formListCol">
                        <div className="inputBody">
                          <label></label>
                          <div style={{ textAlign: "right" }}>
                            {fields && fields.length <= 1 ? (
                              <></>
                            ) : (
                              <Button
                                className="clear-btn"
                                type="button"
                                style={{ margin: "20px 0" }}
                                onClick={() => {
                                  const itemsToRemove = [];
                                  fields.forEach((f, i) => {
                                    if (!findValue(i, "id") && i !== 0) {
                                      itemsToRemove.push(f.name);
                                    }
                                  });
                                  if (itemsToRemove.length > 0) {
                                    remove(itemsToRemove);
                                  }
                                }}
                              >
                                Cancel
                              </Button>
                            )}
                            {canSave ? (
                              <Button
                                className="saveBtn redBtn"
                                style={{ margin: "20px 10px" }}
                                onClick={() => {
                                  form.submit();
                                }}
                                loading={isBtnDisabled}
                              >
                                Save All
                              </Button>
                            ) : (
                              <Button
                                className="saveBtn redBtn"
                                style={{
                                  opacity: "0.5",
                                  pointerEvents: "none",
                                  margin: "0 10px",
                                }}
                                loading={isBtnDisabled}
                              >
                                Save All
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>


                  </>
                )}
              </Form.List>
            </div>
          </div>
        </Form>
      </div>

      {isModalOpen ? (
        <ModalMediaPlayer
          closeModal={handleCancel}
          mediaLink={videoUrl}
          mediaType={mediatype}
        />
      ) : (
        ""
      )}
    </div>
  );
}
